/**
 * Low-level pure lexical helpers for the CREATE / ALTER / DROP TRIGGER
 * extractor (VDE-2 Slice 3). Split out of `trigger.ts` to keep the
 * orchestration core focused — same sibling-split convention as
 * `grant.parse.ts` / `type.parse.ts`.
 *
 * Regex-free (project rule VQE-D10): the qualified-name read, modifier /
 * keyword consumption, event-list scan, and the `EXECUTE FUNCTION fn(args)`
 * capture are all self-contained character-level scans over already-isolated
 * spans — no regex, and no SQL-structure regex into the trigger body.
 *
 * The captured shape is kept byte-faithful to the behavioral contract the
 * removed regex parser upheld (see `parseParkedTriggerStatement.test.ts`): the
 * `QualifiedName` always folds lower-case (not engine-folded), all quote
 * styles (`" "`, `` ` ` ``, `[ ]`) are accepted regardless of source dialect,
 * and the trigger body is carried as one OPAQUE verbatim trimmed substring.
 */

import type { QualifiedName } from '../../../sql-grammar/model';

export interface NameRead {
  readonly name: QualifiedName;
  readonly end: number;
}

const WS = new Set([' ', '\t', '\n', '\r', '\f', '\v']);

function isWs(c: string | undefined): boolean {
  return c !== undefined && WS.has(c);
}

function isIdentStart(c: string | undefined): boolean {
  return c !== undefined && ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || c === '_');
}

function isIdentChar(c: string | undefined): boolean {
  return c !== undefined && (isIdentStart(c) || (c >= '0' && c <= '9') || c === '$');
}

/** Exact `[A-Za-z0-9_]` class — byte-faithful to the removed parser's
 *  `consumeKeyword` word-boundary test (`$` is NOT a word char there). */
function isWordChar(c: string | undefined): boolean {
  return c !== undefined
    && ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9') || c === '_');
}

/** Skip ASCII whitespace forward (no comment handling). Deliberately narrows
 *  to the 6 ASCII whitespace chars rather than JS `\s` (the removed parser's
 *  `skipSpaces`/`trim` matched Unicode whitespace too). SQL never uses
 *  non-ASCII whitespace structurally, so this is a benign, intentional
 *  deviation — NOT a "faithful" copy of the old `\s` behaviour. */
export function skipSpacesLocal(source: string, start: number, end: number): number {
  let i = start;
  while (i < end && isWs(source[i])) i++;
  return i;
}

/** Try to consume the unquoted, word-bounded `keyword` at `i` (case-
 *  insensitive). Returns the index just past it, or `-1` when it does not
 *  match — mirrors the removed parser's `consumeKeyword` (which rejected when
 *  the following char is `[A-Za-z0-9_]`). Engine-quoting agnostic. */
export function consumeWord(
  source: string,
  i: number,
  end: number,
  keyword: string,
): number {
  const klen = keyword.length;
  if (i + klen > end) return -1;
  if (source.slice(i, i + klen).toUpperCase() !== keyword) return -1;
  const next = source[i + klen];
  if (next !== undefined && isWordChar(next)) return -1;
  return i + klen;
}

/** Consume a multi-word phrase whose words are separated by one-or-more
 *  whitespace chars (e.g. `OR REPLACE`, `INSTEAD OF`, `FOR EACH ROW`).
 *  Returns the index just past the final word, or `-1` if any word is
 *  absent. Replicates the removed parser's `\s+`-joined phrase regexes. */
export function consumePhrase(
  source: string,
  i: number,
  end: number,
  words: readonly string[],
): number {
  let p = i;
  for (let w = 0; w < words.length; w++) {
    if (w > 0) {
      const afterWs = skipSpacesLocal(source, p, end);
      if (afterWs === p) return -1;
      p = afterWs;
    }
    const next = consumeWord(source, p, end, words[w]!);
    if (next === -1) return -1;
    p = next;
  }
  return p;
}

function unquoteIdent(raw: string): { name: string; quoted: boolean } {
  if (raw.length >= 2) {
    const first = raw.charAt(0);
    const last = raw.charAt(raw.length - 1);
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return { name: raw.slice(1, -1), quoted: true };
    }
    if (first === '[' && last === ']') {
      return { name: raw.slice(1, -1), quoted: true };
    }
  }
  return { name: raw, quoted: false };
}

/** Read a bare or quoted identifier at `i`, accepting `" "`, `` ` ` `` and
 *  `[ ]` regardless of the source engine — faithful to the removed parser's
 *  `IDENT` alternation. Returns the raw token + the end index, or null. */
function readAnyIdent(
  source: string,
  i: number,
  end: number,
): { raw: string; end: number } | null {
  const c = source[i];
  if (c === '"') {
    let j = i + 1;
    while (j < end && source[j] !== '"') j++;
    if (j >= end) return null;
    return { raw: source.slice(i, j + 1), end: j + 1 };
  }
  if (c === '`') {
    let j = i + 1;
    while (j < end && source[j] !== '`') j++;
    if (j >= end) return null;
    return { raw: source.slice(i, j + 1), end: j + 1 };
  }
  if (c === '[') {
    let j = i + 1;
    while (j < end && source[j] !== ']') j++;
    if (j >= end) return null;
    return { raw: source.slice(i, j + 1), end: j + 1 };
  }
  if (isIdentStart(c)) {
    let j = i + 1;
    while (j < end && isIdentChar(source[j])) j++;
    return { raw: source.slice(i, j), end: j };
  }
  return null;
}

function buildQualifiedName(rawHead: string, rawTail: string | undefined): QualifiedName {
  if (rawTail !== undefined) {
    const head = unquoteIdent(rawHead);
    const tail = unquoteIdent(rawTail);
    return {
      schema: head.name,
      name: tail.name,
      raw: `${rawHead}.${rawTail}`,
      quoted: head.quoted || tail.quoted,
      foldedName: tail.name.toLowerCase(),
      foldedSchema: head.name.toLowerCase(),
    };
  }
  const ident = unquoteIdent(rawHead);
  return {
    name: ident.name,
    raw: rawHead,
    quoted: ident.quoted,
    foldedName: ident.name.toLowerCase(),
  };
}

/** Read a `[schema.]name` qualified name at `i`, byte-faithful to the removed
 *  parser's `QUALIFIED_NAME_RE` (lower-cased folds, all quote styles, `\s*.\s*`
 *  around the dot). Returns null when no identifier starts at `i`. */
export function readTriggerName(
  source: string,
  i: number,
  end: number,
): NameRead | null {
  const head = readAnyIdent(source, i, end);
  if (!head) return null;
  const afterHead = skipSpacesLocal(source, head.end, end);
  if (source[afterHead] === '.') {
    const afterDot = skipSpacesLocal(source, afterHead + 1, end);
    const tail = readAnyIdent(source, afterDot, end);
    if (tail) {
      return { name: buildQualifiedName(head.raw, tail.raw), end: tail.end };
    }
  }
  return { name: buildQualifiedName(head.raw, undefined), end: head.end };
}

/** Read a comma-separated INSERT/UPDATE/DELETE event list at `from`. Returns
 *  the events plus the index just past the last consumed token (mirrors the
 *  removed parser's `parseEvents`, including the bounded 4-iteration loop and
 *  the trailing-separator handling). */
export function readEventList(
  source: string,
  from: number,
  end: number,
): { events: ReadonlyArray<'insert' | 'update' | 'delete'>; consumed: number } {
  const events: Array<'insert' | 'update' | 'delete'> = [];
  let i = from;
  for (let iter = 0; iter < 4; iter++) {
    const ws = skipSpacesLocal(source, i, end);
    const ins = consumeWord(source, ws, end, 'INSERT');
    const upd = ins === -1 ? consumeWord(source, ws, end, 'UPDATE') : -1;
    const del = ins === -1 && upd === -1 ? consumeWord(source, ws, end, 'DELETE') : -1;
    if (ins !== -1) { events.push('insert'); i = ins; }
    else if (upd !== -1) { events.push('update'); i = upd; }
    else if (del !== -1) { events.push('delete'); i = del; }
    else break;
    const sep = skipSpacesLocal(source, i, end);
    if (source[sep] === ',') { i = sep + 1; continue; }
    i = sep;
    break;
  }
  return { events, consumed: i };
}

/** Find the first word-bounded keyword `kw` in `[from, end)` that is at `from`
 *  or preceded by a whitespace char, returning its start index, or `-1`.
 *  Replicates `/(?:^|\s)<kw>\b/i.exec` semantics without regex. When
 *  `requireTrailingWs` is set the keyword must be followed by whitespace
 *  (the removed parser's ` AS\s+` form). */
export function findWordBounded(
  source: string,
  from: number,
  end: number,
  kw: string,
  requireTrailingWs: boolean,
): number {
  const klen = kw.length;
  for (let i = from; i + klen <= end; i++) {
    // Candidate position only at `from` or immediately after whitespace —
    // the `(?:^|\s)` anchor of the removed parser's regex.
    if (i > from && !isWs(source[i - 1])) continue;
    if (source.slice(i, i + klen).toUpperCase() !== kw) continue;
    const next = source[i + klen];
    if (next !== undefined && isWordChar(next)) continue;
    if (requireTrailingWs && !isWs(next)) continue;
    return i;
  }
  return -1;
}

/** Find the first occurrence of the whitespace-joined phrase `words` in
 *  `[from, end)` where the phrase is word-bounded on both ends. Returns
 *  `{ start, end }` of the matched span, or null. Replicates
 *  `/\bW1\s+W2\s+W3\b/i.exec` without regex. */
export function findPhrase(
  source: string,
  from: number,
  end: number,
  words: readonly string[],
): { start: number; end: number } | null {
  const first = words[0]!;
  for (let i = from; i < end; i++) {
    if (source.slice(i, i + first.length).toUpperCase() !== first) continue;
    const prev = source[i - 1];
    if (i > 0 && isWordChar(prev)) continue;
    const matchEnd = consumePhrase(source, i, end, words);
    if (matchEnd === -1) continue;
    const after = source[matchEnd];
    if (after !== undefined && isWordChar(after)) continue;
    return { start: i, end: matchEnd };
  }
  return null;
}

/** Capture an `EXECUTE FUNCTION|PROCEDURE fn(args)` reference inside
 *  `[from, end)`, returning the verbatim `fn(args)` text or null. Byte-
 *  replicates the removed parser's
 *  `/\bEXECUTE\s+(?:FUNCTION|PROCEDURE)\s+([^\s;]+\([^)]*\))/i` capture group
 *  WITHOUT regex — including its greedy-backtrack boundary:
 *
 *  - `[^\s;]+` (greedy) is the maximal run of non-whitespace, non-`;` chars
 *    starting right after `EXECUTE (FUNCTION|PROCEDURE)` + whitespace. That
 *    run may itself contain `(`/`)`.
 *  - To satisfy the trailing `\([^)]*\)`, the engine backtracks the greedy
 *    prefix to the LAST `(` inside that run (with ≥1 prefix char before it)
 *    for which a closing `)` exists somewhere afterward.
 *  - `[^)]*` then matches minimally up to the FIRST `)` at-or-after that
 *    `(`. Because `[^)]*` (unlike the prefix) admits whitespace/`;`, that
 *    closing `)` may lie BEYOND the non-`\s;` prefix run (e.g. the space in
 *    `schema.fn(a, b)`), so it is searched up to `end`, not to the run end.
 *
 *  Net effect, mirroring the regex exactly: `a(b)(c)` → `a(b)(c)`,
 *  `fn(arg(1))` → `fn(arg(1)`, `fn()` → `fn()`, `schema.fn(a, b)` →
 *  `schema.fn(a, b)`; a token with no usable `(`/`)` pair → no match.
 *
 *  Worst case: the LAST-`(` backtrack is O(N²) on a pathological all-`(`,
 *  no-`)` non-`\s;` run — the SAME complexity class as the removed regex's
 *  own backtracking (which is itself superlinear here), on input that cannot
 *  occur in real SQL (statement-bounded; needs a long unbroken paren run with
 *  no whitespace/`;`/`)`). Do NOT "optimise" this into a single forward scan:
 *  it would change the boundary and break the required byte-parity. */
export function captureExecuteFunction(
  source: string,
  from: number,
  end: number,
): string | null {
  for (let i = from; i < end; i++) {
    if (source.slice(i, i + 'EXECUTE'.length).toUpperCase() !== 'EXECUTE') continue;
    if (i > 0 && isWordChar(source[i - 1])) continue;
    const afterExec = i + 'EXECUTE'.length;
    if (!isWs(source[afterExec])) continue;
    let p = skipSpacesLocal(source, afterExec, end);
    let kw = consumeWord(source, p, end, 'FUNCTION');
    if (kw === -1) kw = consumeWord(source, p, end, 'PROCEDURE');
    if (kw === -1) continue;
    p = skipSpacesLocal(source, kw, end);
    // `[^\s;]+` (greedy): maximal non-whitespace, non-`;` run from `tokStart`.
    const tokStart = p;
    let prefEnd = p;
    while (prefEnd < end && !isWs(source[prefEnd]) && source[prefEnd] !== ';') prefEnd++;
    // Greedy backtrack: the LAST `(` strictly inside (tokStart, prefEnd) that
    // has some `)` after it (the `)` may be past prefEnd — `[^)]*` admits
    // whitespace/`;`, so search up to `end`). Then `close` = the FIRST such
    // `)` (minimal `[^)]*`). No qualifying `(` ⇒ regex would not match.
    let close = -1;
    for (let open = prefEnd - 1; open > tokStart; open--) {
      if (source[open] !== '(') continue;
      for (let q = open + 1; q < end; q++) {
        if (source[q] === ')') {
          close = q;
          break;
        }
      }
      if (close !== -1) break;
    }
    if (close === -1) continue;
    return source.slice(tokStart, close + 1);
  }
  return null;
}
